import { useState, useEffect, useCallback, useRef } from 'react';
import { SpeechRecognition, TranscriptMessage } from '@/types/speechRecognition';

export const useBrowserTranscription = (localPhoneNumber?: string) => {
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setIsSupported(true);
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';
      
      recognitionInstance.onresult = (event) => {
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          const newMessage: TranscriptMessage = {
            id: `browser-${Date.now()}`,
            text: finalTranscript.trim(),
            timestamp: new Date(),
            speaker: 'caller',
            source: 'browser',
            confidence: event.results[event.results.length - 1][0].confidence,
            phoneNumber: localPhoneNumber,
            participantType: 'local'
          };
          
          setTranscript(prev => [...prev, newMessage]);
        }
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      recognitionInstance.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current = recognitionInstance;
    } else {
      setIsSupported(false);
    }
  }, []);

  const startTranscription = useCallback(async () => {
    console.log('🎤 [BROWSER] startTranscription called with state:', { 
      recognitionExists: !!recognitionRef.current, 
      isListening, 
      isSupported 
    });
    
    const recognition = recognitionRef.current;
    if (recognition && !isListening) {
      try {
        console.log('🎤 [BROWSER] ⚠️ SKIPPING microphone permission request - already in call');
        console.log('🎤 [BROWSER] 🚀 Starting speech recognition directly...');
        
        recognition.start();
        setIsListening(true);
        console.log('🎤 [BROWSER] ✅ Speech recognition started successfully');
      } catch (error) {
        console.error('🎤 [BROWSER] ❌ Error starting transcription:', error);
        if (error instanceof Error) {
          console.log('🎤 [BROWSER] Error details:', {
            name: error.name,
            message: error.message,
            code: (error as any).code
          });
          
          if (error.name === 'NotAllowedError') {
            console.error('🎤 [BROWSER] 🚫 Microphone permission denied');
          } else if (error.name === 'NotFoundError') {
            console.error('🎤 [BROWSER] 🎤 No microphone found');
          } else {
            console.error('🎤 [BROWSER] 🔴 General transcription error:', error.message);
          }
        }
        throw error; // Re-throw for parent error handling
      }
    } else {
      console.log('🎤 [BROWSER] ⚠️ Cannot start transcription:', { 
        recognitionExists: !!recognition, 
        isListening 
      });
    }
  }, [isListening, isSupported]);

  const stopTranscription = useCallback(() => {
    const recognition = recognitionRef.current;
    if (recognition && isListening) {
      recognition.stop();
      setIsListening(false);
    }
  }, [isListening]);

  const clearTranscript = useCallback(() => {
    setTranscript([]);
  }, []);

  return {
    transcript,
    isListening,
    isSupported,
    startTranscription,
    stopTranscription,
    clearTranscript
  };
};